/**
 * Simulator Configuration Panel
 * Allows users to configure simulation parameters
 */

import React, { useState, useEffect } from 'react';
import {
  X, Settings, Users, Clock, Package, Box, Truck,
  MapPin, ClipboardList, RefreshCw, Shield, Zap, Database, ShoppingCart, Warehouse, Loader2, Edit3
} from 'lucide-react';
import { SimulatorConfig, WorkflowType } from '../types';
import { AuthService } from '../../../../../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

interface WarehouseOption {
  warehouseID: string;
  warehouseName: string;
}

interface SimulatorConfigPanelProps {
  config: SimulatorConfig;
  onSave: (config: Partial<SimulatorConfig>) => void;
  onCancel: () => void;
}

interface RuleGroupOption {
  ruleGroupID: number;
  ruleGroupName: string;
  ruleGroupCode?: string;
}

export const SimulatorConfigPanel: React.FC<SimulatorConfigPanelProps> = ({
  config,
  onSave,
  onCancel
}) => {
  const [localConfig, setLocalConfig] = useState<SimulatorConfig>({ ...config });
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [ruleGroups, setRuleGroups] = useState<RuleGroupOption[]>([]);
  const [loadingRuleGroups, setLoadingRuleGroups] = useState(true);

  // Fetch available warehouses on mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!config.companyNo) {
        console.warn('No companyNo available for fetching warehouses');
        setLoadingWarehouses(false);
        return;
      }

      try {
        const response = await AuthService.authenticatedFetch(`${API_BASE_URL}/${config.companyNo}/warehouse`);
        if (response.ok) {
          const data = await response.json();
          setWarehouses(data);

          // Auto-select first warehouse if none selected
          if (!localConfig.warehouseId && data.length > 0) {
            const firstWarehouse = data[0].warehouseID || data[0].WarehouseID;
            setLocalConfig(prev => ({ ...prev, warehouseId: firstWarehouse }));
          }
        } else {
          console.error('Failed to fetch warehouses:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Failed to fetch warehouses:', error);
      } finally {
        setLoadingWarehouses(false);
      }
    };

    fetchWarehouses();
  }, [config.companyNo]);

  // Fetch available rule groups on mount
  useEffect(() => {
    const fetchRuleGroups = async () => {
      if (!config.companyNo) {
        console.warn('No companyNo available for fetching rule groups');
        setLoadingRuleGroups(false);
        return;
      }

      try {
        const response = await AuthService.authenticatedFetch(`${API_BASE_URL}/${config.companyNo}/rulegroupengine/rulegroups`);
        if (response.ok) {
          const data = await response.json();
          // Filter to only picking-related rule groups (typically have "plukk" or "picking" in name)
          const pickingGroups = data.filter((rg: RuleGroupOption) =>
            rg.ruleGroupName?.toLowerCase().includes('plukk') ||
            rg.ruleGroupName?.toLowerCase().includes('picking') ||
            rg.ruleGroupCode?.toLowerCase().includes('pick') ||
            rg.ruleGroupName?.toLowerCase().includes('ordre') // Also include order-related
          );
          // If no specific picking groups found, show all
          setRuleGroups(pickingGroups.length > 0 ? pickingGroups : data);
        } else {
          console.error('Failed to fetch rule groups:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Failed to fetch rule groups:', error);
      } finally {
        setLoadingRuleGroups(false);
      }
    };

    fetchRuleGroups();
  }, [config.companyNo]);

  const handleWarehouseChange = (warehouseId: string) => {
    setLocalConfig(prev => ({ ...prev, warehouseId }));
  };

  const handleRuleGroupChange = (ruleGroupId: number) => {
    setLocalConfig(prev => ({
      ...prev,
      workflows: {
        ...prev.workflows,
        picking: {
          ...prev.workflows.picking,
          ruleGroupId
        }
      }
    }));
  };

  const handleOperatorCountChange = (value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      operatorCount: Math.max(1, Math.min(10, value))
    }));
  };

  const handleWorkflowToggle = (workflow: WorkflowType) => {
    setLocalConfig(prev => ({
      ...prev,
      workflows: {
        ...prev.workflows,
        [workflow]: {
          ...prev.workflows[workflow],
          enabled: !prev.workflows[workflow].enabled
        }
      }
    }));
  };

  const handleTimingChange = (key: keyof typeof localConfig.timing, value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      timing: {
        ...prev.timing,
        [key]: Math.max(100, value)
      }
    }));
  };

  const handleSafetyChange = (key: keyof typeof localConfig.safety, value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      safety: {
        ...prev.safety,
        [key]: value
      }
    }));
  };

  const handleWorkflowIntervalChange = (value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      workflowIntervalMs: Math.max(500, value)
    }));
  };

  // Data generation handlers
  const handlePickingOrdersToGenerate = (value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      workflows: {
        ...prev.workflows,
        picking: {
          ...prev.workflows.picking,
          ordersToGenerate: Math.max(0, Math.min(50, value))
        }
      }
    }));
  };

  const handleReceivingOrdersToGenerate = (value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      workflows: {
        ...prev.workflows,
        receiving: {
          ...prev.workflows.receiving,
          ordersToGenerate: Math.max(0, Math.min(50, value))
        }
      }
    }));
  };

  const handleSave = () => {
    onSave(localConfig);
  };

  const workflowConfig: { type: WorkflowType; label: string; icon: React.FC<{ className?: string }> }[] = [
    { type: 'picking', label: 'Plukking', icon: Package },
    { type: 'packing', label: 'Pakking', icon: Box },
    { type: 'receiving', label: 'Mottak', icon: Truck },
    { type: 'putaway', label: 'Put-Away', icon: MapPin },
    { type: 'counting', label: 'Telling', icon: ClipboardList },
    { type: 'transfer', label: 'Transfer', icon: RefreshCw },
    { type: 'correction', label: 'Korreksjon', icon: Edit3 }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-800">Simulator Innstillinger</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Warehouse Selection - REQUIRED */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
            <div className="flex items-center gap-2 mb-4">
              <Warehouse className="w-5 h-5 text-indigo-600" />
              <h3 className="font-medium text-gray-800">Lager</h3>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Påkrevd</span>
            </div>

            {loadingWarehouses ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Laster lagre...</span>
              </div>
            ) : warehouses.length === 0 ? (
              <p className="text-sm text-red-600">Ingen lagre funnet. Opprett et lager først.</p>
            ) : (
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Velg lager for simulering:
                </label>
                <select
                  value={localConfig.warehouseId}
                  onChange={(e) => handleWarehouseChange(e.target.value)}
                  className="w-full px-4 py-2.5 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-800 font-medium"
                >
                  <option value="">-- Velg lager --</option>
                  {warehouses.map((wh) => (
                    <option key={wh.warehouseID} value={wh.warehouseID}>
                      {wh.warehouseID} - {wh.warehouseName}
                    </option>
                  ))}
                </select>
                {!localConfig.warehouseId && (
                  <p className="mt-2 text-sm text-amber-600">
                    Du må velge et lager før simulering kan starte.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Operators Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-gray-800">Operatører</h3>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-600">Antall virtuelle operatører:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleOperatorCountChange(localConfig.operatorCount - 1)}
                  className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                >
                  -
                </button>
                <span className="w-8 text-center font-medium">{localConfig.operatorCount}</span>
                <button
                  onClick={() => handleOperatorCountChange(localConfig.operatorCount + 1)}
                  className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                >
                  +
                </button>
              </div>
              <span className="text-xs text-gray-500">(1-10)</span>
            </div>
          </div>

          {/* Workflows Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-gray-800">Workflows</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {workflowConfig.map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  onClick={() => handleWorkflowToggle(type)}
                  className={`p-3 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                    localConfig.workflows[type].enabled
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${
                    localConfig.workflows[type].enabled ? 'text-purple-600' : 'text-gray-400'
                  }`} />
                  <span className={`font-medium ${
                    localConfig.workflows[type].enabled ? 'text-purple-700' : 'text-gray-500'
                  }`}>
                    {label}
                  </span>
                  <div className={`ml-auto w-4 h-4 rounded-full border-2 ${
                    localConfig.workflows[type].enabled
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300'
                  }`}>
                    {localConfig.workflows[type].enabled && (
                      <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="currentColor">
                        <path d="M10.293 3.293a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L4.5 7.586l4.293-4.293a1 1 0 0 1 1.414 0z" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Data Generation Section */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-amber-600" />
              <h3 className="font-medium text-gray-800">Auto-Generering av Data</h3>
            </div>
            <p className="text-sm text-amber-700 mb-4">
              Konfigurer hvor mange ordre som skal genereres automatisk under simulering når det ikke finnes nok data.
            </p>

            {/* Rule Group Selection for Picking */}
            <div className="bg-white rounded-lg p-4 border border-amber-200 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="w-4 h-4 text-purple-600" />
                <label className="block text-sm font-medium text-gray-700">
                  Regelgruppe for Plukking
                </label>
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Påkrevd</span>
              </div>
              {loadingRuleGroups ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Laster regelgrupper...</span>
                </div>
              ) : ruleGroups.length === 0 ? (
                <p className="text-sm text-red-600">Ingen regelgrupper funnet. Opprett regelgrupper først.</p>
              ) : (
                <div>
                  <select
                    value={localConfig.workflows.picking.ruleGroupId || 0}
                    onChange={(e) => handleRuleGroupChange(parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      !localConfig.workflows.picking.ruleGroupId ? 'border-amber-300' : 'border-gray-300'
                    }`}
                  >
                    <option value={0}>-- Velg regelgruppe --</option>
                    {ruleGroups.map((rg) => (
                      <option key={rg.ruleGroupID} value={rg.ruleGroupID}>
                        {rg.ruleGroupCode ? `${rg.ruleGroupCode} - ` : ''}{rg.ruleGroupName}
                      </option>
                    ))}
                  </select>
                  {!localConfig.workflows.picking.ruleGroupId && (
                    <p className="mt-2 text-sm text-amber-600">
                      Du må velge en regelgruppe for å opprette plukklister automatisk.
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Regelgruppen bestemmer hvordan plukklister opprettes fra salgsordrer.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Sales Orders (Picking) */}
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="w-4 h-4 text-blue-600" />
                  <label className="block text-sm font-medium text-gray-700">
                    Salgsordrer (Plukking)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={localConfig.workflows.picking.ordersToGenerate}
                    onChange={(e) => handlePickingOrdersToGenerate(parseInt(e.target.value) || 0)}
                    min={0}
                    max={50}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                  <span className="text-sm text-gray-500">ordre</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Opprettes hvis ingen plukklister er tilgjengelige
                </p>
              </div>

              {/* Purchase Orders (Receiving) */}
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-4 h-4 text-green-600" />
                  <label className="block text-sm font-medium text-gray-700">
                    Innkjøpsordrer (Mottak)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={localConfig.workflows.receiving.ordersToGenerate}
                    onChange={(e) => handleReceivingOrdersToGenerate(parseInt(e.target.value) || 0)}
                    min={0}
                    max={50}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                  <span className="text-sm text-gray-500">ordre</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Opprettes hvis ingen mottaksordrer er tilgjengelige
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-amber-100/50 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Tips:</strong> Sett til 0 for å bare bruke eksisterende data.
                Ordre genereres kun når workflowen starter og finner at det mangler data.
              </p>
            </div>
          </div>

          {/* Timing Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-gray-800">Tidsinnstillinger (ms)</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TimingInput
                label="Plukktid"
                value={localConfig.timing.pickTimeMs}
                onChange={(v) => handleTimingChange('pickTimeMs', v)}
              />
              <TimingInput
                label="Pakketid"
                value={localConfig.timing.packTimeMs}
                onChange={(v) => handleTimingChange('packTimeMs', v)}
              />
              <TimingInput
                label="Mottakstid"
                value={localConfig.timing.receiveTimeMs}
                onChange={(v) => handleTimingChange('receiveTimeMs', v)}
              />
              <TimingInput
                label="Plassering"
                value={localConfig.timing.placeTimeMs}
                onChange={(v) => handleTimingChange('placeTimeMs', v)}
              />
              <TimingInput
                label="Telletid"
                value={localConfig.timing.countTimeMs}
                onChange={(v) => handleTimingChange('countTimeMs', v)}
              />
              <TimingInput
                label="Gangtid"
                value={localConfig.timing.walkTimeMs}
                onChange={(v) => handleTimingChange('walkTimeMs', v)}
              />
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <TimingInput
                label="Workflow-intervall"
                value={localConfig.workflowIntervalMs}
                onChange={handleWorkflowIntervalChange}
                description="Tid mellom hver workflow-iterasjon"
              />
            </div>
          </div>

          {/* Safety Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-red-600" />
              <h3 className="font-medium text-gray-800">Sikkerhetsgrenser</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Maks transaksjoner per sesjon
                </label>
                <input
                  type="number"
                  value={localConfig.safety.maxTransactionsPerSession}
                  onChange={(e) => handleSafetyChange('maxTransactionsPerSession', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Maks varighet (minutter)
                </label>
                <input
                  type="number"
                  value={localConfig.safety.maxDurationMinutes}
                  onChange={(e) => handleSafetyChange('maxDurationMinutes', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Lås-opprydding (minutter)
                </label>
                <input
                  type="number"
                  value={localConfig.safety.lockCleanupIntervalMinutes}
                  onChange={(e) => handleSafetyChange('lockCleanupIntervalMinutes', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            Lagre Innstillinger
          </button>
        </div>
      </div>
    </div>
  );
};

// Timing Input Component
interface TimingInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  description?: string;
}

const TimingInput: React.FC<TimingInputProps> = ({ label, value, onChange, description }) => (
  <div>
    <label className="block text-sm text-gray-600 mb-1">{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      step={100}
      min={100}
      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
    />
    {description && (
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    )}
  </div>
);

export default SimulatorConfigPanel;
